import { useCallback } from 'react';
import {
  useDatalayerTracking,
  useTracking,
} from '@edreamsodigeo/frontend-core';
import { useFlowTools } from 'flowTools/useFlowTools';
import { usePrime } from 'clientState/providers/PrimeProvider';
import { useHighlightedItinerariesQuery } from '../FilteredItinerariesProvider';
import { usePerformedSearchRequest } from '../SearchRequestProvider';
import { useSortingTypeLabel } from '@components/Sorting/Provider';
import useAppliedFiltersCounter from '@components/Filters/Filters/useAppliedFiltersCounter';
import { RESULTS_CATEGORY } from '../TrackingProviders';
import { CARBON_FOOTPRINT_DIMENSION } from '../../hooks/useSetSearchGACustomDimensions/config';
import useGetBuyPathDeal, {
  DEAL_WEEKEND,
} from 'clientState/hooks/useGetBuyPathDeal';

import { getDiscount } from 'clientState/hooks/useItineraryListTracking/createDiscountPillLabel';
import getHasCardDecoration from 'clientState/hooks/useItineraryListTracking/getHasCardDecoration';
import {
  useTrackingInfo,
  useFlightOfTheDayItineraryIds,
} from 'clientState/providers/SearchResponseProvider';
import { useTrackingDecoration } from 'clientState/providers/TrackingDecorationProvider';
import { useFlightOfTheDayTracking } from '@components/Results/Components/Itinerary/useFlightOfTheDayTracking';
import { HighlightedType } from '@components/Results/Components/ItineraryHeader/constants/highlightedTypes';

const ONE_WAY = 'ONE_WAY';
const MULTIPLE_DESTINATIONS = 'MULTIPLE_DESTINATIONS';

const NOT_APPLY_LABEL = 'NA';
const DEPARTURE_CARRIERS_PREFIX = 'da:';
const RETURN_CARRIERS_PREFIX = 'ra:';

const getPriceAmount = (itinerary) =>
  itinerary ? Math.round(itinerary.price.amount) : NOT_APPLY_LABEL;

const getPricePosition = (itinerary) =>
  itinerary ? itinerary.priceSortingPosition : NOT_APPLY_LABEL;

const highlightedTypeToLabel = {
  recommended: 'o',
  withBaggage: 'b',
  fastest: 'f',
  cheapest: 'c',
};

const decorationTypeToNumber = {
  wallet: '1',
  priceFreeze: '2',
  fastest: '3',
  cheapest: '4',
  personalised: '5',
  genius: '6',
};

const getSelectedItineraryTypeLabel = (itinerary) => {
  return 'box:' + (highlightedTypeToLabel[itinerary.highlightedType] || '0');
};

const getItineraryLabel = (itinerary, prefix) =>
  `${prefix}po:${getPricePosition(itinerary)}-${prefix}pr:${getPriceAmount(
    itinerary
  )}`;

const getSelectedItineraryLabel = (itinerary) => {
  const pricePositionLabel = getPricePosition(itinerary);
  const priceLabel = getPriceAmount(itinerary);
  const typeLabel = getSelectedItineraryTypeLabel(itinerary);
  return [`pos:${pricePositionLabel}`, `pri:${priceLabel}`, typeLabel].join(
    '-'
  );
};

const getHighlightedListLabel = ({
  recommended,
  cheapest,
  fastest,
  withBaggage,
}) => {
  return [
    getItineraryLabel(recommended, 'o'),
    getItineraryLabel(cheapest, 'c'),
    getItineraryLabel(fastest, 's'),
    getItineraryLabel(withBaggage, 'b'),
  ].join('_');
};

const getRecommendedSelectionLabel = (
  itinerary,
  filterApplied,
  highlightedItineraries
) => {
  return [
    's',
    getSelectedItineraryLabel(itinerary),
    getHighlightedListLabel(highlightedItineraries),
    `filter:${filterApplied ? 1 : 0}`,
    'vp:0',
  ];
};

const getPrimeDaySelectionLabel = (itinerary) => {
  const type = itinerary.campaignConfig?.primeDayConfig?.primeDayFareType;
  return ['prime-day-label', `click:${type.toLowerCase()}`, `pag:res`];
};

const getRecommendedPriceAmount = ({ recommended }) =>
  recommended ? recommended.price.amount : NOT_APPLY_LABEL;

const trackOnClickCardWithDecorationLabel = (
  itinerary,
  trackEvent,
  isDesktop,
  trackingInfo
) => {
  const { hasDecoration, decorationType } = getHasCardDecoration(
    itinerary,
    isDesktop,
    trackingInfo
  );

  if (hasDecoration) {
    let decoration = decorationType;

    if (decorationType === 'recommended') {
      decoration = !trackingInfo?.returningUserDeviceId
        ? 'genius'
        : 'personalised';
    }

    const label = [
      'flight-label',
      `click:${decorationTypeToNumber[decoration] || '0'}`,
      'box:NA',
      'pag:res',
    ];

    trackEvent({
      category: RESULTS_CATEGORY,
      action: 'flight_labels',
      label: label.join('_'),
    });
  } else {
    trackEvent({
      category: RESULTS_CATEGORY,
      action: 'flight_labels',
      label: 'flight-label_click_0_box:NA_pag:res',
    });
  }
};

const getItineraryOnClickLabel = (
  itinerary,
  tripType = '',
  sortingTypeLabel,
  highlightedItineraries,
  position,
  searchRequest
) => {
  let trackingElements = [
    'sel',
    `p:${getTransportTypeLabel(itinerary)}`,
    `res:${itinerary.priceSortingPosition}`,
    `tab:${sortingTypeLabel}`,
    `pos:${position + 1}`,
    `prc:${itinerary.price.amount}`,
    `rec:${getRecommendedPriceAmount(highlightedItineraries)}`,
    `type:${tripType
      .split('_')
      .reduce((type, str) => type + str.charAt(0), '')}`,
  ];

  if (tripType !== MULTIPLE_DESTINATIONS) {
    trackingElements = trackingElements.concat(
      getCarriersTrackingLabel(itinerary, tripType)
    );
    trackingElements = trackingElements.concat(
      getRouteTrackingLabel(searchRequest)
    );
  }

  trackingElements = trackingElements.concat(`dis:${getDiscount(itinerary)}`);

  return trackingElements;
};

const getHighlightedLabel = (highlightedTypeOriginal) => {
  return highlightedTypeOriginal === 'recommended' ? 0 : 1;
};

const getTrendLabel = (trend) => {
  if (!trend) return 'so';

  const trends = {
    down: 'ch',
    up: 'ex',
    equal: 'eq',
  };

  return trends[trend];
};

const getTabLabel = (tab) => {
  const tabs = {
    recom: 'recom',
    price: 'cheap',
    duration: 'fast',
  };

  return tabs[tab];
};

const getDealItineraryOnClickLabel = ({
  action,
  itinerary,
  position,
  tab,
  isFilterApplied,
}) => {
  const dealType = action === 'weekend-deals' ? 'wd' : 'hd';

  return [
    action,
    'click',
    `${dealType}pos:${position + 1}`,
    `gd:${getHighlightedLabel(itinerary.dealInfo?.highlightedTypeOriginal)}`,
    `pr:${getTrendLabel(itinerary.dealInfo?.trend)}`,
    `tab:${getTabLabel(tab)}`,
    `filt:${isFilterApplied ? 1 : 0}`,
    `pag:flres`,
  ];
};
export const useIsFilterApplied = () => {
  const appliedFiltersCounter = useAppliedFiltersCounter();
  return appliedFiltersCounter > 0;
};

const getFlightNumbers = (itinerary, tripType) => {
  const selectedSegments = itinerary.legs.map(({ segments }) => segments[0]);
  return selectedSegments.reduce((label, { sections }, index) => {
    const flightCodes = sections.map(({ flightCode }) => flightCode).join(',');
    const tripTypeLabel = tripType === 'multipleTrip' ? `_m${index}:` : `_r:`;
    return !index
      ? `${label}${flightCodes}`
      : `${label}${tripTypeLabel}${flightCodes}`;
  }, 'd:');
};

const getCarriersPerSegment = (segment, index) => {
  const labelPrefix =
    index === 0 ? DEPARTURE_CARRIERS_PREFIX : RETURN_CARRIERS_PREFIX;
  const carriersSet = new Set(segment.carriers.map((carrier) => carrier.id));
  return `${labelPrefix}${Array.from(carriersSet).join('-')}`;
};

const getCarriersTrackingLabel = (itinerary, tripType) => {
  let carriersTrackingLabel = itinerary.legs.map(({ segments }, index) =>
    getCarriersPerSegment(segments[0], index)
  );
  if (tripType === ONE_WAY) {
    carriersTrackingLabel.push(RETURN_CARRIERS_PREFIX.concat(NOT_APPLY_LABEL));
  }
  return carriersTrackingLabel;
};

const getRouteTrackingLabel = (searchRequest) => {
  const segments = searchRequest.itinerary?.segments;
  return `r:${segments[0]?.departure?.iata}-${segments[0]?.destination?.iata}`;
};

const getTransportTypeLabel = ({ transportTypes }) => {
  if (transportTypes.length > 1) {
    return 'xfaring';
  }
  return transportTypes[0] === 'PLANE' ? 'flight' : 'train';
};

const getHotelVoucherSelectionLabel = (
  itinerary,
  position,
  isHotelVoucher,
  tripType,
  searchRequest,
  isPrimeUser
) => {
  let carriersTrackingLabel = itinerary.legs.map(({ segments }, index) =>
    getCarriersPerSegment(segments[0], index)
  );

  return [
    `hotel-vouchers-select:${isHotelVoucher ? '1' : '0'}`,
    `A:${carriersTrackingLabel}`,
    `TT:${tripType.split('_').reduce((type, str) => type + str.charAt(0), '')}`,
    `PT:${isPrimeUser ? 'Y' : 'N'}`,
    `R:${getRouteTrackingLabel(searchRequest)}`,
    `R:${position + 1}`,
  ].join('_');
};

const useItineraryTracking = () => {
  const { trackPage, trackEvent, setCustomDimension } = useTracking();
  const { save: saveDatalayer } = useDatalayerTracking();
  const isFilterApplied = useIsFilterApplied();
  const searchRequest = usePerformedSearchRequest();
  const { data: highlightedItineraries } = useHighlightedItinerariesQuery();
  const sortingTypeLabel = useSortingTypeLabel();
  const { isMobile } = useFlowTools();
  const dealBuyPath = useGetBuyPathDeal();
  const { isPrimeUser = false } = usePrime() || {};
  const { isDesktop } = useFlowTools();
  const trackingInfo = useTrackingInfo();
  const { hasPersonalisedDecoration } = useTrackingDecoration();
  const flightOfTheDayItineraryIds = useFlightOfTheDayItineraryIds();
  const { trackFlightOfTheDay } = useFlightOfTheDayTracking('click');

  const tripType = searchRequest ? searchRequest.tripType : '';

  const sendTracking = useCallback(
    (itinerary, position, isHotelVoucher) => {
      if (isMobile) {
        trackPage('summary', {
          funnel: true,
          customDimensions: {
            dimension61: getFlightNumbers(itinerary, tripType),
          },
        });
        trackEvent({
          category: RESULTS_CATEGORY,
          action: 'click-hotel-voucher',
          label: getHotelVoucherSelectionLabel(
            itinerary,
            position,
            isHotelVoucher,
            tripType,
            searchRequest,
            isPrimeUser
          ),
        });
        if (
          flightOfTheDayItineraryIds.includes(itinerary.id) &&
          itinerary.highlightedType !== HighlightedType.Recommended
        ) {
          trackFlightOfTheDay(itinerary, position, true);
        }
      }

      if (hasPersonalisedDecoration) {
        trackOnClickCardWithDecorationLabel(
          itinerary,
          trackEvent,
          isDesktop,
          trackingInfo
        );
      }
      if (
        isDesktop &&
        flightOfTheDayItineraryIds.includes(itinerary.id) &&
        itinerary.highlightedType !== HighlightedType.Recommended
      ) {
        trackFlightOfTheDay(itinerary, position, true);
      }
      if (itinerary.transportTypes) {
        trackEvent({
          category: RESULTS_CATEGORY,
          action: itinerary.transportTypes.includes('TRAIN')
            ? 'trains_results'
            : 'searcher_flights',
          label: getItineraryOnClickLabel(
            itinerary,
            tripType,
            sortingTypeLabel,
            highlightedItineraries,
            position,
            searchRequest
          ).join('_'),
        });
      }
      trackEvent({
        category: RESULTS_CATEGORY,
        action: 'rec',
        label: getRecommendedSelectionLabel(
          itinerary,
          isFilterApplied,
          highlightedItineraries
        ).join('_'),
      });
      if (itinerary.campaignConfig?.primeDayConfig?.primeDayFareType) {
        trackEvent({
          category: RESULTS_CATEGORY,
          action: 'prime_day',
          label: getPrimeDaySelectionLabel(itinerary).join('_'),
        });
      }
      if (itinerary.dealInfo) {
        const action =
          dealBuyPath === DEAL_WEEKEND ? 'weekend-deals' : 'hottest-deals';

        trackEvent({
          category: RESULTS_CATEGORY,
          action: action,
          label: getDealItineraryOnClickLabel({
            action,
            itinerary,
            position,
            tab: sortingTypeLabel,
            isFilterApplied,
          }).join('_'),
        });
      }
      if (itinerary.carbonFootprint) {
        setCustomDimension(
          CARBON_FOOTPRINT_DIMENSION,
          `${itinerary.carbonFootprint.isEco}`
        );
        saveDatalayer({
          flight_low_CO2: `${itinerary.carbonFootprint.isEco}`,
        });
      }
    },
    [
      isMobile,
      hasPersonalisedDecoration,
      trackEvent,
      tripType,
      sortingTypeLabel,
      highlightedItineraries,
      searchRequest,
      isFilterApplied,
      trackPage,
      isPrimeUser,
      isDesktop,
      trackingInfo,
      dealBuyPath,
      setCustomDimension,
      saveDatalayer,
      flightOfTheDayItineraryIds,
      trackFlightOfTheDay,
    ]
  );
  return sendTracking;
};

export default useItineraryTracking;
